import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { useTranslation } from "react-i18next";
import {
	useCanGoBack,
	useNavigate,
	useRouter,
	useSearch,
} from "@tanstack/react-router";
import EyeIcon from "../assets/icons/EyeIcon";
import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { postLogin, saveUserForNotifications } from "../api/api";
import { AxiosError } from "axios";
import { Browser } from "@capacitor/browser";
import { clearAuthData } from "../utils/storage/authStorage";
import { useAuth } from "@/context/AuthContext";
import OneSignal from "onesignal-cordova-plugin";
import { Capacitor } from "@capacitor/core";

export const Login = (): JSX.Element => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const router = useRouter();
	const canGoBack = useCanGoBack();
	const { login } = useAuth();
	const search = useSearch({ from: "/(auth)/_auth/login" });

	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [showPassword, setShowPassword] = useState(false);
	const [errorMessage, setErrorMessage] = useState<string | null>(null);

	const { mutate } = useMutation({
		mutationKey: ["login", email, password],
		mutationFn: () => postLogin({ email, password }),
		onError: async (error: unknown) => {
			if (error instanceof AxiosError && error.response?.status === 401) {
				setErrorMessage(t("login.unauthorized"));
				if (import.meta.env.VITE_ONE_SIGNAL_FEATURE_FLAG === "true" && Capacitor.isNativePlatform()) {
				OneSignal.logout();
				}
				await clearAuthData();
			} else {
				setErrorMessage(t("login.something_went_wrong"));
			}
		},
		onSuccess: (data) => {
			login(data.access_token, data.user.email)
				.then(async () => {
					if (search.returnTo) {
						await navigate({ to: search.returnTo });
					} else if (canGoBack) {
						router.history.back();
					} else {
						navigate({ to: "/" }).catch((error) => {
							console.error("Error navigating to home:", error);
						});
					}
				})
				.then(async () => {
					try {
						await saveUserForNotifications(data.user.email);
					} catch (error) {
						console.error("Error saving user for notifications:", error);
					}
				})
				.catch((error) => {
					console.error("login error", error);
				});
		},
	});

	const openRegisterSite = async (): Promise<void> => {
		await Browser.open({ url: "https://akademialtca.pl/#register" });
	};

	const openResetPasswordSite = async (): Promise<void> => {
		await Browser.open({ url: "https://akademialtca.pl/#reset" });
	};

	return (
		<>
			<div className="h-full mx-auto grid w-[350px] gap-6 pt-[67px]">
				<div className="text-center">
					<h1 className="text-[28px] text-[#212B36] font-medium">
						{t("login.title")}
					</h1>
				</div>
				<div className="text-sm text-center text-[#637381]">
					Logowanie za pomocą danych z portalu Akademia LTCA
				</div>
				<div className="grid gap-4">
					<div className="grid gap-2">
						<Input
							required
							className="h-14 border-none shadow-none bg-[#919EAB14] placeholder:text-[#919EAB] placeholder:text-sm"
							id="email"
							placeholder={t("login.email_placeholder")}
							type="email"
							value={email}
							onChange={(event) => {
								setEmail(event.target.value);
							}}
						/>
					</div>
					<div className="grid gap-2">
						<div className="relative w-full">
							<Input
								required
								className="h-14 border-none shadow-none bg-[#919EAB14] placeholder:text-[#919EAB] placeholder:text-sm"
								id="password"
								placeholder={t("login.password_placeholder")}
								type={showPassword ? "text" : "password"}
								value={password}
								onChange={(event) => {
									setPassword(event.target.value);
								}}
							/>
							<button
								onClick={() => {
									setShowPassword(!showPassword);
								}}
							>
								<EyeIcon className="absolute top-5 right-2" />
							</button>
						</div>
						<div className="flex items-end">
							<button
								className="ml-auto inline-block text-sm font-normal underline text-[#637381]"
								onClick={openResetPasswordSite}
							>
								{t("login.forgot_password")}
							</button>
						</div>
					</div>
					{errorMessage && (
						<div className="bg-[#FFEAEA] text-[#B00020] p-3 rounded-md text-sm text-center">
							{errorMessage}
						</div>
					)}
					<Button
						className="w-full bg-[#282F41]"
						type="submit"
						onClick={() => {
							mutate();
						}}
					>
						{t("login.login_button")}
					</Button>
					<Button
						className="w-full bg-[#919EAB14] shadow-none text-[#212B36]"
						type="submit"
						onClick={() => navigate({ to: "/" })}
					>
						{t("login.without_login")}
					</Button>
				</div>
			</div>
			<div className="flex text-sm justify-center w-full mb-10">
				<p className="text-[#637381] ">{t("login.without_account")}</p>
				<button
					className="text-[#196DFE] font-semibold ml-1 cursor-pointer"
					onClick={openRegisterSite}
				>
					{t("login.register")}
				</button>
			</div>
		</>
	);
};
